import {SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {getReadBucket, repoURL} from '@Libraries/s3-library';
import {getAssetUrl} from '@Utils/s3.util';
import {assertNever} from '@Utils/assert-never.util';

const PLATFORM_LOGO_FOLDER = 'assets';
const PLATFORM_LOGO_MODIFIED_TIMESTAMP = 1792148450;

export const PMW_SOCIAL_MEDIA_URL_FACEBOOK = 'https://www.facebook.com/postermywall';
export const PMW_SOCIAL_MEDIA_URL_INSTAGRAM = 'https://www.instagram.com/postermywall/';
export const PMW_SOCIAL_MEDIA_URL_TWITTER = 'https://www.twitter.com/postermywall';
export const PMW_SOCIAL_MEDIA_URL_YOUTUBE = 'https://www.youtube.com/postermywall';

const platformLogoURL = (fileName: string): string => {
  return repoURL(`${PLATFORM_LOGO_FOLDER}/${fileName}?ts=${PLATFORM_LOGO_MODIFIED_TIMESTAMP}`, getReadBucket());
};

export const getSocialIconURLByType = (socialType: SocialPlatform, platformMediaType: PlatformMediaType): string => {
  const baseURL = getAssetUrl('social-icons/original/', true);
  let imageName: string;

  switch (socialType) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_GROUP:
      if (platformMediaType === PlatformMediaType.STORIES) {
        return platformLogoURL('facebook-stories-logo-thumb-circle.png');
      }

      imageName = 'social-icon-facebook.png';
      break;
    case SocialPlatform.TWITTER:
      return getAssetUrl('social-icons/circle-black/social-icon-x.png', true);
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      imageName = 'social-icon-linkedin.png';
      break;
    case SocialPlatform.TIKTOK:
      imageName = 'social-icon-tiktok.png';
      break;
    case SocialPlatform.YOUTUBE:
      imageName = 'social-icon-youtube.png';
      break;
    case SocialPlatform.INSTAGRAM:
      if (platformMediaType === PlatformMediaType.STORIES) {
        return platformLogoURL('instagram-stories-logo-thumb-circle.png');
      }

      imageName = 'social-icon-instagram.png';
      break;
    case SocialPlatform.PINTEREST:
      imageName = 'social-icon-pinterest.png';
      break;
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return platformLogoURL('google-business-profile-logo-thumb-circle.png');
    case SocialPlatform.THREADS:
      return platformLogoURL('threads-logo-thumb-circle-v3.png');
    default:
      return '';
  }

  return baseURL + imageName;
};

export const getDisplayableSocialPlatformURL = (type: SocialPlatform): string => {
  switch (type) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_GROUP:
      return 'facebook.com';
    case SocialPlatform.TWITTER:
      return 'x.com';
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return 'linkedin.com';
    case SocialPlatform.TIKTOK:
      return 'tiktok.com';
    case SocialPlatform.YOUTUBE:
      return 'youtube.com';
    case SocialPlatform.INSTAGRAM:
      return 'instagram.com';
    case SocialPlatform.PINTEREST:
      return 'pinterest.com';
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return 'google.com/business';
    case SocialPlatform.THREADS:
      return 'threads.net';
    default:
      return '';
  }
};

export const getRedirectableSocialPlatformURL = (type: SocialPlatform): string => {
  switch (type) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_GROUP:
      return 'https://www.facebook.com';
    case SocialPlatform.TWITTER:
      return 'https://www.twitter.com';
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return 'https://www.linkedin.com';
    case SocialPlatform.TIKTOK:
      return 'https://www.tiktok.com';
    case SocialPlatform.YOUTUBE:
      return 'https://www.youtube.com';
    case SocialPlatform.INSTAGRAM:
      return 'https://www.instagram.com';
    case SocialPlatform.PINTEREST:
      return 'https://www.pinterest.com';
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return 'https://www.google.com/business';
    case SocialPlatform.THREADS:
      return 'https://www.threads.net/';
    default:
      return '';
  }
};

export const getPlatformMediaTypeLabel = (platformMediaType: PlatformMediaType): string => {
  switch (platformMediaType) {
    case PlatformMediaType.POST:
      return window.i18next.t('pmwjs_post');
    case PlatformMediaType.STORIES:
      return window.i18next.t('pmwjs_story');
    case PlatformMediaType.REELS:
      return window.i18next.t('pmwjs_reel');
    default:
      return assertNever(platformMediaType);
  }
};

export const getSocialPlatformDetailedName = (type: SocialPlatform): string => {
  switch (type) {
    case SocialPlatform.FACEBOOK:
      return 'Facebook';
    case SocialPlatform.FACEBOOK_PROFILE:
      return window.i18next.t('pmwjs_facebook_profile');
    case SocialPlatform.FACEBOOK_PAGE:
      return window.i18next.t('pmwjs_facebook_page');
    case SocialPlatform.FACEBOOK_GROUP:
      return window.i18next.t('pmwjs_facebook_group');
    case SocialPlatform.TWITTER:
      return 'X (formerly Twitter)';
    case SocialPlatform.LINKEDIN:
      return 'LinkedIn';
    case SocialPlatform.LINKEDIN_PAGE:
      return window.i18next.t('pmwjs_linkedin_page');
    case SocialPlatform.LINKEDIN_PROFILE:
      return window.i18next.t('pmwjs_linkedin_profile');
    case SocialPlatform.TIKTOK:
      return 'TikTok';
    case SocialPlatform.YOUTUBE:
      return 'YouTube';
    case SocialPlatform.INSTAGRAM:
      return 'Instagram';
    case SocialPlatform.PINTEREST:
      return 'Pinterest';
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return 'Business Profile';
    case SocialPlatform.THREADS:
      return 'Threads';
    default:
      return '';
  }
};

export const getSocialPlatformName = (type: SocialPlatform): string => {
  switch (type) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_GROUP:
      return 'Facebook';
    case SocialPlatform.TWITTER:
      return 'X (formerly Twitter)';
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return 'LinkedIn';
    case SocialPlatform.TIKTOK:
      return 'TikTok';
    case SocialPlatform.YOUTUBE:
      return 'YouTube';
    case SocialPlatform.INSTAGRAM:
      return 'Instagram';
    case SocialPlatform.PINTEREST:
      return 'Pinterest';
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return 'Google Business Profile';
    case SocialPlatform.THREADS:
      return 'Threads';
    default:
      return '';
  }
};

/**
 * Collapses a stored account platform onto the canonical platform it belongs to — one of the nine
 * `SocialUniquePlatforms` the UI offers. A user who connected a Facebook page (`fb_page`) or a LinkedIn
 * company page (`linkedin-page`) is connected to Facebook / LinkedIn as far as any per-platform UI is
 * concerned. Mirrors PHP's `SocialPlatform::getUniqueSocialPlatform()`.
 *
 * The `default` returns the value unchanged rather than asserting exhaustiveness: every other platform is
 * already canonical, and account types arrive over the wire, where the backend enum carries members this
 * one does not (`instagram`, `x`).
 */
export const getComparableSocialPlatform = (type: SocialPlatform): SocialPlatform => {
  switch (type) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
      return SocialPlatform.FACEBOOK;
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return SocialPlatform.LINKEDIN;
    default:
      return type;
  }
};

/** The distinct platforms the accounts post to, in account order: a Facebook page and a Facebook profile are one platform. */
export const getUniqueSocialPlatformsForAccounts = (accounts: SocialAccount[]): SocialPlatform[] => {
  return [...new Set(accounts.map((account) => getComparableSocialPlatform(account.type)))];
};

/** The first platform's name and how many more there are, e.g. "Facebook, +2 more...". */
export const getSocialPlatformsSummary = (platforms: SocialPlatform[]): string => {
  const [firstPlatform] = platforms;
  if (firstPlatform === undefined) {
    return '';
  }

  if (platforms.length === 1) {
    return getSocialPlatformDetailedName(firstPlatform);
  }

  return window.i18next.t('pmwjs_platform_plus_x_more', {platform: getSocialPlatformDetailedName(firstPlatform), number: platforms.length - 1});
};

const getInstagramProfileThumbnailByMediaType = (platformMediaType: PlatformMediaType): string => {
  if (platformMediaType === PlatformMediaType.STORIES) {
    return platformLogoURL('instagram-stories-logo-thumb-circle.png');
  }

  return platformLogoURL('instagram-logo-thumb-circle.png');
};

const getFacebookProfileThumbnailByMediaType = (platformMediaType: PlatformMediaType): string => {
  if (platformMediaType === PlatformMediaType.STORIES) {
    return platformLogoURL('facebook-stories-logo-thumb-circle.png');
  }

  return platformLogoURL('facebook-logo-thumb-circle.png');
};

export const getCircularSocialIconURLByType = (socialType: SocialPlatform, platformMediaType: PlatformMediaType): string => {
  switch (socialType) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_GROUP:
      return getFacebookProfileThumbnailByMediaType(platformMediaType);
    case SocialPlatform.TWITTER:
      return platformLogoURL('x-logo-thumb-circle.png');
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return platformLogoURL('linkedin-logo-thumb-circle.png');
    case SocialPlatform.TIKTOK:
      return platformLogoURL('tiktok-logo-thumb-circle.png');
    case SocialPlatform.YOUTUBE:
      return platformLogoURL('youtube-thumb-logo-icon.png');
    case SocialPlatform.INSTAGRAM:
      return getInstagramProfileThumbnailByMediaType(platformMediaType);
    case SocialPlatform.PINTEREST:
      return platformLogoURL('pinterest-logo-thumb-circle.png');
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return platformLogoURL('google-business-profile-logo-thumb-circle.png');
    case SocialPlatform.THREADS:
      return platformLogoURL('threads-logo-thumb-circle-v3.png');
    default:
      return '';
  }
};

const FACEBOOK_AVATAR_HOSTS: readonly RegExp[] = [/^graph\.facebook\.com$/, /^platform-lookaside\.fbsbx\.com$/, /^scontent[a-z0-9.-]*\.fbcdn\.net$/];
const INSTAGRAM_AVATAR_HOSTS: readonly RegExp[] = [/^scontent[a-z0-9.-]*\.cdninstagram\.com$/, /^scontent[a-z0-9.-]*\.fbcdn\.net$/];

/** The image hosts a platform serves its account avatars from; regional CDN nodes match by their naming scheme, never a whole domain. */
const getAccountAvatarHosts = (platform: SocialPlatform): readonly RegExp[] => {
  switch (platform) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
      return FACEBOOK_AVATAR_HOSTS;
    case SocialPlatform.INSTAGRAM:
    case SocialPlatform.THREADS:
      return INSTAGRAM_AVATAR_HOSTS;
    case SocialPlatform.TWITTER:
      return [/^pbs\.twimg\.com$/, /^abs\.twimg\.com$/];
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return [/^media\.licdn\.com$/];
    case SocialPlatform.YOUTUBE:
      return [/^yt3\.ggpht\.com$/, /^yt3\.googleusercontent\.com$/];
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return [/^lh[3-6]\.googleusercontent\.com$/];
    case SocialPlatform.TIKTOK:
      return [/^p\d+-[a-z0-9-]+\.tiktokcdn(?:-us|-eu)?\.com$/];
    case SocialPlatform.PINTEREST:
      return [/^i\.pinimg\.com$/];
    default:
      return assertNever(platform);
  }
};

export const isSocialAccountAvatarHost = (host: string): boolean => {
  return Object.values(SocialPlatform).some((platform) => getAccountAvatarHosts(platform).some((avatarHost) => avatarHost.test(host)));
};
